import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, ref, effectScope } from 'vue'
import { useRouter } from 'vue-router'

import { notificationToken } from 'src/composables/fcm/fcmToken.state'

import { authService } from 'src/services/api/auth.service'
import { unsubscribeFromAllTopics, clearTokenCache } from 'src/services/api/firebase.service'
import { idbService } from 'src/services/app/idb.service'
import { log } from 'src/services/app/log.service'
import { notifyService } from 'src/services/app/notify.service'
import { storageService } from 'src/services/app/storage.service'
import { STORAGE_KEYS } from 'src/services/consts/api.const'
import { NOTIFICATION_MESSAGES } from 'src/services/consts/notification.const'
import { ROUTES } from 'src/services/consts/routes.const'
import { hasPermission } from 'src/services/helpers/auth.helper'

// Create global state outside the composable (exported for module-level consumers e.g. useFCM)
export const authUser = ref(null)

// Read-only users (readonly bit, no user bit) may not write. Fail closed: a logged-in user
// without a permissions string is treated as read-only.
export const isReadOnly = computed(() => {
  const user = authUser.value
  if (!user?.id) return false
  if (!user.permissions) return true
  return !hasPermission('user')
})

const redirectRoute = ref(null)
let loginMutation = null
let googleLoginMutation = null

// Create a global effect scope
const scope = effectScope()

export function useAuth() {
  const router = useRouter()
  const queryClient = useQueryClient()

  const isLoggedIn = computed(() => !!authUser.value?.id)
  const isLoading = computed(() => loginMutation?.isPending)
  const isGoogleLoading = computed(() => googleLoginMutation?.isPending)

  const initializeFromStorage = async () => {
    const storedUser = storageService.get(STORAGE_KEYS.LOGGED_IN_USER)
    if (storedUser && !authUser.value) {
      await setUser(storedUser)
    }
  }

  const setUser = async (user) => {
    authUser.value = user
    storageService.set(STORAGE_KEYS.LOGGED_IN_USER, user)
    await queryClient.setQueryData([STORAGE_KEYS.LOGGED_IN_USER], user)
  }

  const logout = async (options = { clearRedirect: true }) => {
    try {
      // Get data before clearing storage
      const user = authUser.value

      // Unsubscribe from Firebase notifications if user exists
      if (user?.id) {
        if (notificationToken.value) {
          await unsubscribeFromAllTopics(notificationToken.value, user.id)
        }
        clearTokenCache()
      }
    } catch (error) {
      log.error('Error unsubscribing during logout:', error)
    }

    // Clear all storage
    queryClient.clear()
    idbService.removeData(STORAGE_KEYS.COMPANIES)
    localStorage.clear()
    storageService.clearAllSession()

    authUser.value = null
    if (options.clearRedirect) redirectRoute.value = null

    // Navigate to login
    router.push('/' + ROUTES.LOGIN)
  }

  const handleSuccessLogin = async (user) => {
    await setUser(user)
    await queryClient.invalidateQueries([STORAGE_KEYS.COMPANIES])
    const redirectTo = redirectRoute.value || '/'
    redirectRoute.value = null
    router.push(redirectTo)
    notifyService.success(NOTIFICATION_MESSAGES.loginSuccess + user.displayName)
  }

  const handleFailureLogin = (error) => {
    log.error('Login failed:', error)
    authUser.value = null
    storageService.remove(STORAGE_KEYS.LOGGED_IN_USER)
    notifyService.error(NOTIFICATION_MESSAGES.loginError)
  }

  // Initialize mutations in the effect scope if they haven't been created yet
  if (!loginMutation || !googleLoginMutation) {
    scope.run(() => {
      loginMutation = useMutation({
        mutationFn: (credentials) => authService.login(credentials),
        onSuccess: handleSuccessLogin,
        onError: handleFailureLogin,
        retry: false,
      })

      googleLoginMutation = useMutation({
        mutationFn: (token) => authService.googleLogin(token),
        onSuccess: handleSuccessLogin,
        onError: handleFailureLogin,
        retry: false,
      })
    })
  }

  initializeFromStorage()
  return {
    user: authUser,
    redirectRoute: redirectRoute,
    isLoading: isLoading.value,
    isGoogleLoading: isGoogleLoading.value,
    isLoggedIn,
    isReadOnly,

    login: loginMutation?.mutate,
    googleLogin: googleLoginMutation?.mutate,
    logout,
    initializeFromStorage,
    setRedirectRoute: (route) => (redirectRoute.value = route),
  }
}
